import { Link } from "~/components/ui/link";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/button";
import fleetSolutionsBlack from "~/assets/logos/bouncie-fleet-solutions-black.svg";
import fleetSolutionsWhite from "~/assets/logos/bouncie-fleet-solutions-white.svg";
import { useIsFleetHeader } from "~/components/header/header-mode";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
// Fleet's Platform column uses its own crops of these three product shots
// (not the Products subnav's originals): those have large transparent margins
// baked in, which left the device looking tiny in Fleet's small thumb frame.
const navDriveImg = imageUrl("/images/products/nav-drive-fleet.png");
const navDashcamImg = imageUrl("/images/products/nav-dashcam-fleet.png");
const navGoImg = imageUrl("/images/products/nav-go-fleet.png");
const fleetCardImg = imageUrl("/images/subnav/fleet-fuel-costs.jpg");
import {
    ColumnButtons,
    ColumnDivider,
    SubnavPanelBody,
    IconNavItem,
    NavCard,
    NavColumn,
    NavThumb,
} from "~/components/header/full-header/subnavigation/common";
import { RiArrowRightSLine } from "@remixicon/react";
import { imageUrl } from "~/lib/image-url";

export default function FleetNavItem({ onNavigate }: { onNavigate: () => void }) {
    const { t } = useTranslation("common");
    const fleet = useIsFleetHeader();

    const industries: IconNavItem[] = [
        { name: t("subnav.fleet.industry_list.trade_services"),      href: "/fleet" },
        { name: t("subnav.fleet.industry_list.delivery_logistics"),  href: "/fleet" },
        { name: t("subnav.fleet.industry_list.rentals"),             href: "/fleet/rental" },
        { name: t("subnav.fleet.industry_list.turo_hosts"),          href: "/fleet/turo" },
        { name: t("subnav.fleet.industry_list.charitable"),          href: "/fleet/non-profit" },
        { name: t("subnav.fleet.industry_list.government"),          href: "/fleet/municipal" },
    ];

    const products: IconNavItem[] = [
        { decorationLeft: <NavThumb src={navDriveImg}   alt={t("subnav.products.drive.name")} fit="contain" />,   name: t("subnav.products.drive.name"), description: t("subnav.products.drive.description"),    href: "/fleet/products/drive" },
        { decorationLeft: <NavThumb src={navDashcamImg} alt={t("subnav.products.view.name")} fit="contain" />, name: t("subnav.products.view.name"),  description: t("subnav.products.view.description"),     href: "/products/view" },
        { decorationLeft: <NavThumb src={navGoImg}      alt={t("subnav.products.go.name")} fit="contain" />,   name: t("subnav.products.go.name"),    description: t("subnav.products.go.description_short"), href: "/products/go" },
    ];

    return (
        <SubnavPanelBody
            footer={
                <div className="flex w-full items-center justify-between">
                    <img src={fleet ? fleetSolutionsWhite : fleetSolutionsBlack} alt="Bouncie" className="h-6" />
                    <div className="flex items-center gap-3">
                        <Button asChild variant={fleet ? "secondary-alt" : "secondary"} size={"sm"} className="w-[190px]">
                            <Link to="/contact" onClick={onNavigate}>{t("nav.contact_sales")}</Link>
                        </Button>
                        <Button asChild variant={fleet ? "primary-alt" : "primary"} size={"sm"} className="w-[190px]">
                            <Link to="/fleet/quote" onClick={onNavigate}>{t("nav.build_quote")}</Link>
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="flex w-full items-start gap-[37px]">
                <div className="flex items-start gap-4">
                    <NavColumn header={t("subnav.fleet.col_fleet_solutions")} className="w-[283px]">
                        <Button asChild variant="link" className="fleet:text-text-body-tertiary h-auto px-2 py-2 text-base tracking-[0.16px] underline">
                            <Link to="/fleet" onClick={onNavigate}>
                                {t("subnav.fleet.all_solutions")}
                                <RiArrowRightSLine className="h-5 w-5" />
                            </Link>
                        </Button>
                    </NavColumn>
                    <ColumnDivider />
                    <NavColumn header={t("subnav.fleet.col_industries")} className="w-[255px]">
                        <ColumnButtons onNavigate={onNavigate} buttonData={industries}/>
                    </NavColumn>
                    <ColumnDivider />
                    <NavColumn header={t("subnav.fleet.col_platform")} className="w-[292px]">
                        <ColumnButtons onNavigate={onNavigate} buttonData={products}/>
                    </NavColumn>
                </div>
                <NavCard
                    className="hidden w-[253px] xl:flex"
                    tone="dark"
                    image={fleetCardImg}
                    title={t("subnav.fleet.card_title")}
                    description={t("subnav.fleet.card_description")}
                />
            </div>
        </SubnavPanelBody>
    );
}
